import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { Save } from 'lucide-react';

const card = { background: 'var(--field)', border: '1px solid var(--rule-strong)', borderRadius: 16, padding: '16px 18px', marginBottom: 12 };
const inp = { padding: '8px 12px', background: 'var(--paper-sunk)', border: '1px solid var(--rule-strong)', borderRadius: 8, color: 'var(--ink)', fontSize: 'var(--t-note)', width: '100%', boxSizing: 'border-box' };
const lbl = { fontSize: 'var(--t-note)', fontWeight: 600, color: 'var(--ink-mute)', marginBottom: 4, display: 'block' };

// ファネル: キャッチ→ティッシュ→トーク開始→フルトーク→着座→獲得（ADR-0002）
const RULE_LABELS = {
  catch_volume: 'キャッチ絶対数（未達なら警告）',
  catch_to_sticker: 'キャッチ→ティッシュ回収率',
  sticker_to_talk: 'ティッシュ→トーク率',
  talk_to_fulltalk: 'トーク→フルトーク率',
  fulltalk_to_seat: 'フルトーク→着座率',
  talk_to_seat: 'トーク→着座率（旧・フルトーク導入前）',
  seat_to_acq: '着座→獲得率',
  meter_missing_rate: '検針票なし率',
  family_lost_rate: '家族確認失注率',
};

export default function AdminThresholds() {
  const [rows, setRows] = useState([]);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(null);

  const load = async () => {
    const data = await api.getThresholds();
    setRows(data);
    const e = {};
    data.forEach(r => {
      // 率(0.0〜1.0)はパーセント表示(0〜100)で扱う
      const isRate = r.rule_key !== 'catch_volume';
      e[r.id] = {
        min_volume: r.min_volume,
        threshold_pct: isRate ? Math.round(r.threshold_pct * 100 * 10) / 10 : r.threshold_pct,
        message: r.message,
        theme: r.theme,
        is_active: r.is_active,
      };
    });
    setEdits(e);
  };
  useEffect(() => { load(); }, []);

  const setField = (id, key, val) => setEdits(p => ({ ...p, [id]: { ...p[id], [key]: val } }));

  const handleSave = async (id) => {
    setSaving(id);
    try {
      const row = rows.find(r => r.id === id);
      const isRate = row?.rule_key !== 'catch_volume';
      const payload = {
        ...edits[id],
        threshold_pct: isRate ? (Number(edits[id].threshold_pct) || 0) / 100 : Number(edits[id].threshold_pct) || 0,
      };
      await api.updateThreshold(id, payload);
      await load();
    } catch (e) { alert(e.message); }
    setSaving(null);
  };

  return (
    <div style={{ maxWidth: 880, margin: '0 auto', padding: 'var(--s6) var(--s4) 120px' }}>
      <h1 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 800, color: 'var(--ink)'}}> ボトルネック基準</h1>
      <p style={{ margin: '0 0 20px', fontSize: 'var(--t-note)', color: 'var(--ink-soft)' }}>各指標が基準を下回ると「ボトルネック診断」に表示されます。</p>
      {rows.map(r => {
        const e = edits[r.id] || {};
        const isRate = r.rule_key !== 'catch_volume';
        return (
          <div key={r.id} style={{ ...card, border: `1px solid ${e.is_active ? '#33406E' : 'var(--rule-strong)'}`, opacity: e.is_active ? 1 : 0.5 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)' }}>{RULE_LABELS[r.rule_key] || r.rule_key}</div>
              <button onClick={() => setField(r.id, 'is_active', e.is_active ? 0 : 1)} style={{ fontSize: 'var(--t-note)', padding: '4px 12px', borderRadius: 8, border: `1px solid ${e.is_active ? '#1E5540' : 'var(--rule-strong)'}`, background: 'transparent', color: e.is_active ? 'var(--gain)' : 'var(--ink-mute)', cursor: 'pointer' }}>
                {e.is_active ? '有効' : '無効'}
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
              <div>
                <label style={lbl}>最低母数（件未満でスキップ）</label>
                <input type="number" style={inp} value={e.min_volume ?? ''} onChange={ev => setField(r.id, 'min_volume', Number(ev.target.value))} />
              </div>
              <div>
                <label style={lbl}>警告基準 {isRate ? '（%）' : '（件数）'}</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="number" style={{ ...inp, paddingRight: isRate ? 32 : 12 }}
                    step={isRate ? '1' : '1'}
                    min="0"
                    max={isRate ? '100' : undefined}
                    value={e.threshold_pct ?? ''}
                    onChange={ev => setField(r.id, 'threshold_pct', ev.target.value === '' ? '' : Number(ev.target.value))}
                  />
                  {isRate && <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--ink-mute)', fontSize: 'var(--t-note)', fontWeight: 700, pointerEvents: 'none' }}>%</span>}
                </div>
                <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-soft)', marginTop: 4 }}>
                  {isRate ? 'この値を下回ったらボトルネックとして検出' : '稼働中の最低キャッチ数'}
                </div>
              </div>
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={lbl}>メッセージ</label>
              <input type="text" style={inp} value={e.message || ''} onChange={ev => setField(r.id, 'message', ev.target.value)} />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={lbl}>改善テーマ</label>
              <input type="text" style={inp} value={e.theme || ''} onChange={ev => setField(r.id, 'theme', ev.target.value)} />
            </div>
            <button onClick={() => handleSave(r.id)} disabled={saving === r.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: 'var(--grad-primary)', border: 'none', borderRadius: 8, color: '#fff', fontWeight: 700, fontSize: 'var(--t-note)', cursor: 'pointer', opacity: saving === r.id ? 0.7 : 1 }}>
              <Save size={13} /> {saving === r.id ? '保存中...' : '保存'}
            </button>
          </div>
        );
      })}
    </div>
  );
}
